/* THE PUREVERR PAGE CONTROL — ONE ADAPTIVE CONTROL, SHARED BY EVERY GALLERY.
   NEW FILE, 2026-09-19 (UI-polish brief I): "Reuse or refactor the shared component primitives so FilterPro gets the
   same draggable/scrubbable horizontal page control beneath the media ... No slider library and no duplicated second
   interaction model."
   This is the page control that was inside assets/pureverr-pdp.js, MOVED here with one change: the selectors it used to
   hard-code are now options, so a second gallery can hand it its own markup instead of a second implementation being
   written. Its looks are assets/pureverr-pagecontrol.css.

   WHO CALLS IT
   - assets/pureverr-pdp.js     -> every <media-gallery> on the FaucetPro PDP, with the base theme's selectors.
   - assets/pureverr-filterpro.js -> the FilterPro gallery, with its own list and slide selectors.
   Both pass markup that carries the SAME dot classes (`.slider-buttons--dots`, `.slider-counter__link--dots`, `.dot`),
   because the control reads and writes the active state through those class names and the stylesheet draws them.

   WHAT THE CALLER OWES IT
   - a container, a dot bar inside it, a horizontally scrolling list, and one dot per visible slide;
   - keeping the active dot in step with the scroll position. On the FaucetPro PDP the base theme's slider JS does that;
     on the FilterPro page assets/pureverr-filterpro.js does it from its own scroll handler. The control marks dots
     itself only DURING a scrub, where waiting for a scroll sync would lag the finger.
   If the dots and the visible slides do not pair one to one, it does nothing at all and the caller's own controls are
   left exactly as they were.

   Presentation and navigation only. Nothing here creates, reorders, edits or duplicates a product image.
   Every entry point is guarded; if this file fails, the dots are still real buttons and still work as the theme
   shipped them. */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

  /* =============================================================================================
     THE PAGE CONTROL
     ---------------------------------------------------------------------------------------------
     The deck stays the base theme's native scroller - overflow-x, scroll-snap, the browser's own
     momentum - so a swipe on the IMAGE is untouched and follows the finger 1:1. The base theme's slider
     JS already keeps the dots in step with it (the active class and aria-current, measured) and scrolls
     to a dot on click. This adds only what Apple's page control does and that one does not:

     TAP        Touch or pen: one page toward the side of the current dot that was tapped, animated.
                HIG Page controls: "Tapping on the leading or trailing side of the current-page
                indicator reveals the next or previous page". A mouse targets the dot under it, which is
                the HIG's pointer behaviour. Either way the animated scroll is the platform's own.
     SCRUB      Touch and drag. Finger x over the row of dots -> progress 0..1 -> page index. Crossing a
                page boundary selects that page AT ONCE: scrollLeft is written with snap and smooth
                scrolling off, so a fast scrub never plays a slide animation for each image it passes.
                HIG: "Avoid animating page transitions during scrubbing. People can scrub very quickly,
                and using the scrolling animation for every transition can make your app lag and cause
                distracting visual flashes." Past either end it pins to the first or last page.
     RELEASE    The selection is already exactly on its snap point, so snap comes back on without
                moving anything; the lens settles back into a dot and the glass contracts.
     KEYBOARD   The dots are real buttons: Enter/Space shows that image, as the theme shipped.
                Left/Right/Home/End move between images and focus follows. Roving tabindex, so the
                control is a single tab stop.
     ============================================================================================= */
  var SCRUB_START_PX = 6;  /* horizontal travel before a press becomes a scrub */
  var SETTLE_MS = 120;     /* matches --pv-pc-settle: the lens settles back into a dot ... */
  var HOLD_MS = 90;        /* ... and the glass stays up this much longer, then contracts */

  /* opts: { bar, list, slide } — CSS selectors, resolved inside `root`. The defaults are the base theme's product
     gallery, so assets/pureverr-pdp.js calls initControl(el) exactly as it did before this file existed. */
  function initControl(root, opts) {
    if (!root) return;
    var o = opts || {};
    var bar = root.querySelector(o.bar || '.slider-buttons.slider-buttons--dots');
    var list = root.querySelector(o.list || '.product__media-list.slider');
    if (!bar || !list || bar.dataset.pvPager === '1') return;
    var dots = [].slice.call(bar.querySelectorAll('.slider-counter__link--dots'));
    var slides = [].slice.call(list.querySelectorAll(o.slide || '.product__media-item')).filter(function (s) {
      return getComputedStyle(s).display !== 'none';
    });
    /* the dots and the visible slides must pair one to one, or this leaves the theme's control alone */
    if (dots.length < 2 || dots.length !== slides.length) return;
    bar.dataset.pvPager = '1';
    var n = dots.length;

    /* each slide's resting scrollLeft, read from the DOM on demand (never cached across a resize): the
       first slide rests at 0, so every other rests at its distance from the first */
    function rests() {
      var max = Math.max(0, list.scrollWidth - list.clientWidth);
      var o = slides[0].offsetLeft;
      return slides.map(function (s) { return clamp(s.offsetLeft - o, 0, max); });
    }
    function nearest(x) {
      var r = rests(), best = 0;
      for (var i = 1; i < n; i++) if (Math.abs(r[i] - x) < Math.abs(r[best] - x)) best = i;
      return best;
    }
    /* a tap or a key that is still animating has a destination; a second tap steps on from there */
    var pending = -1, pendingTimer = 0;
    function current() { return pending >= 0 ? pending : nearest(list.scrollLeft); }

    function setTabs(a) {
      dots.forEach(function (d, k) { d.tabIndex = k === a ? 0 : -1; });
    }
    function syncTabs() {
      for (var k = 0; k < n; k++) if (dots[k].classList.contains('slider-counter__link--active')) return setTabs(k);
      setTabs(0);
    }
    /* A scrub shows its selection at once, in the same frame, rather than waiting for the theme's scroll
       sync - which lands on the same dot a moment later, because the deck is already exactly there. */
    function mark(i) {
      dots.forEach(function (d, k) {
        var on = k === i;
        d.classList.toggle('slider-counter__link--active', on);
        if (on) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current');
      });
    }
    /* Animated navigation, for taps and keys only. The theme's own scroll sync moves the dot as the
       deck passes the halfway point, exactly as it does for a swipe, so nothing is marked here. */
    function go(i) {
      i = clamp(i, 0, n - 1);
      pending = i;
      clearTimeout(pendingTimer);
      pendingTimer = setTimeout(function () { pending = -1; }, 800);
      var to = rests()[i];
      try { list.scrollTo({ left: to, behavior: reduce.matches ? 'auto' : 'smooth' }); }
      catch (e) { list.scrollLeft = to; }
    }
    function snapOff() { list.style.scrollSnapType = 'none'; list.style.scrollBehavior = 'auto'; }
    function snapOn() { list.style.scrollSnapType = ''; list.style.scrollBehavior = ''; }

    /* The row's box, taken once per press. Every dot is the same width (--pv-pc-pitch), so progress
       along the row maps straight onto a page. */
    function rowBox() {
      var a = dots[0].getBoundingClientRect(), b = dots[n - 1].getBoundingClientRect();
      return { left: a.left, width: Math.max(1, b.right - a.left) };
    }
    function indexAt(x, box) {
      var p = clamp((x - box.left) / box.width, 0, 1);
      return Math.min(n - 1, Math.floor(p * n));
    }

    /* Progressive enhancement only, and never the thing that communicates a detent: iOS Safari has no
       Vibration API at all (MDN browser-compat-data, navigator.vibrate: safari_ios not supported), so
       on an iPhone this does nothing and the visual detents carry the feedback. Called only when a
       scrub the visitor is performing changes the page - never for a swipe on the image. No permission
       is asked for, and a reduced-motion or save-data preference turns it off. */
    function tick() {
      if (reduce.matches || typeof navigator.vibrate !== 'function') return;
      if (navigator.connection && navigator.connection.saveData) return;
      try { navigator.vibrate(8); } catch (e) { /* unsupported or blocked: the detents still show it */ }
    }

    var press = null, holdTimer = 0, clickGuard = false, guardTimer = 0;

    function onDown(e) {
      if (press) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      clearTimeout(holdTimer);
      press = { id: e.pointerId, type: e.pointerType, x0: e.clientX, scrubbing: false, box: rowBox(), sel: current() };
      bar.classList.add('is-pressed');
      try { bar.setPointerCapture(e.pointerId); } catch (err) { /* older engines: moves still arrive while over the bar */ }
    }
    function onMove(e) {
      if (!press || e.pointerId !== press.id) return;
      if (!press.scrubbing) {
        if (Math.abs(e.clientX - press.x0) < SCRUB_START_PX) return;
        press.scrubbing = true;
        bar.classList.add('is-scrubbing');
        snapOff();
      }
      var i = indexAt(e.clientX, press.box);
      if (i === press.sel) return;
      press.sel = i;
      pending = -1;
      list.scrollLeft = rests()[i];
      mark(i);
      tick();
    }
    function onEnd(e) {
      if (!press || e.pointerId !== press.id) return;
      var p = press;
      press = null;
      try { bar.releasePointerCapture(e.pointerId); } catch (err) {}
      /* the click that follows a pointer gesture is ours, not the theme's - see the capture listener */
      clickGuard = true;
      clearTimeout(guardTimer);
      guardTimer = setTimeout(function () { clickGuard = false; }, 450);
      if (p.scrubbing) {
        setTabs(p.sel);
        /* the deck is already on the selected snap point; putting snap back cannot move it */
        requestAnimationFrame(snapOn);
      } else if (e.type === 'pointerup') {
        var cur = current(), target = cur;
        if (p.type === 'mouse') {
          target = indexAt(e.clientX, p.box);
        } else {
          var c = dots[cur].getBoundingClientRect();
          if (e.clientX < c.left) target = cur - 1;
          else if (e.clientX > c.right) target = cur + 1;
        }
        target = clamp(target, 0, n - 1);
        if (target !== cur) go(target);
      }
      bar.classList.remove('is-scrubbing');
      holdTimer = setTimeout(function () { bar.classList.remove('is-pressed'); }, p.scrubbing ? SETTLE_MS + HOLD_MS : HOLD_MS);
    }

    bar.addEventListener('pointerdown', onDown);
    bar.addEventListener('pointermove', onMove);
    bar.addEventListener('pointerup', onEnd);
    bar.addEventListener('pointercancel', onEnd);
    bar.addEventListener('lostpointercapture', onEnd);
    /* Capture phase, so it runs before the theme's own dot handler. A click the pointer handlers above
       already acted on is stopped here; a keyboard click (detail 0) and an assistive-technology click
       that never went through them pass straight to the theme. */
    bar.addEventListener('click', function (e) {
      if (!clickGuard || e.detail === 0) return;
      clickGuard = false;
      e.preventDefault();
      e.stopPropagation();
    }, true);

    bar.addEventListener('keydown', function (e) {
      var k = e.key, cur = current(), to = null;
      if (k === 'ArrowRight') to = cur + 1;
      else if (k === 'ArrowLeft') to = cur - 1;
      else if (k === 'Home') to = 0;
      else if (k === 'End') to = n - 1;
      if (to === null || !e.target.closest || !e.target.closest('.slider-counter__link--dots')) return;
      e.preventDefault();
      to = clamp(to, 0, n - 1);
      if (to !== cur) go(to);
      setTabs(to);
      try { dots[to].focus({ preventScroll: true }); } catch (err) { dots[to].focus(); }
    });

    /* a swipe on the image ends any pending tap destination */
    list.addEventListener('pointerdown', function () { pending = -1; }, { passive: true });
    syncTabs();
    try {
      new MutationObserver(syncTabs).observe(bar, { attributes: true, subtree: true, attributeFilter: ['class'] });
    } catch (e) {}
  }

  window.PureverrPageControl = { init: initControl };
})();
